import {
  Option,
  SelectBox,
  SelectBoxProps,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { GetEvidenceQuery } from "../evidence.api.types";
import { EvidenceQuery } from "../evidence.query";
import { Evidence } from "../evidence.types";

export const EvidenceTooltipContent = styled(
  ({ className, data }: { className?: string; data: Evidence }) => {
    return (
      <div className={className}>
        <div className="detail-title">Evidence Details</div>
        <div className="detail-item">
          <div className="detail-label">Evidence Number</div>
          <div className="detail-value">{data.evidence_number || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Item Name</div>
          <div className="detail-value">{data.model_name || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Provider</div>
          <div className="detail-value">{data.manufacturer || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Model/Service</div>
          <div className="detail-value">{data.model_number || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Unique ID</div>
          <div className="detail-value">{data.serial_number || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Description</div>
        </div>
        <div className="detail-value">{data.description || "None"}</div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 300px;

  .detail-title {
    font-weight: bold;
    font-size: 15px;
  }
  .detail-item {
    display: flex;
    flex-direction: column;
  }

  .detail-label {
    font-weight: bold;
    min-width: 150px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const EvidenceOptionComponent = ({
  option,
}: {
  option: Partial<Option>;
}) => {
  const theme = useTheme();
  return (
    <div>
      <div>{option.label}</div>
      <div style={{ color: theme.palette.text.secondary }}>
        {[option.data?.manufacturer, option.data?.model_name].join(" | ")}
      </div>
    </div>
  );
};

export type EvidenceSelectorProps = Omit<
  SelectBoxProps,
  | "data"
  | "renderOption"
  | "OptionTooltip"
  | "searchFn"
  | "onScroll"
  | "searchable"
  | "loading"
  | "defaultValue"
  | "value"
> & {
  valueKey?: "evidence_id" | "uuid";
  defaultValue?: Option;
  value?: Option;
  disabledValues?: (string | number)[];
  query?: GetEvidenceQuery;
};

export const EvidenceSelector: React.FC<EvidenceSelectorProps> = (props) => {
  const { valueKey = "evidence_id", query: queryProps, ...rest } = props;
  const [search, setSearch] = useState("");

  const query = {
    search,
    data_only: false,
    pageSize: 100,
    ...queryProps,
  };

  const {
    data,
    isLoading,
    isError,
    error,
    isFetched,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = EvidenceQuery.useGetInfiniteEvidence(query);

  const records =
    data?.pages?.reduce<Evidence[]>((acc, page) => {
      return [...acc, ...(page?.data || [])];
    }, []) || [];

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (isFetchingNextPage) return;
    if (!hasNextPage) return;
    fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback(
    (val: string) => setSearch(val || ""),
    300
  );

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <=
      clientHeight + 100 * ((data?.pages?.length as number) || 0)
    ) {
      debouncedFetchNextPage();
    }
  };

  // Combine number and name in label for client-side search by either
  const options: Option[] = records.map((item) => ({
    label: item.evidence_number,
    value: valueKey === "evidence_id" ? item.evidence_id : item.uuid,
    disabled: props.disabledValues?.includes(
      valueKey === "evidence_id" ? item.evidence_id : item.uuid
    ),
    data: item,
  }));

  return (
    <SelectBox
      label="Linked Evidence"
      placeholder="Select or search for evidence..."
      {...props}
      data={options}
      onScroll={handleScroll}
      searchable={true}
      searchFn={debouncedSearch}
      loading={isLoading}
      renderOption={(item) => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        }
        return <EvidenceOptionComponent option={item} />;
      }}
      OptionTooltip={EvidenceTooltipContent}
    />
  );
};
